import React, { useState, useEffect } from "react";
import "./attendance.css";
import Api from "../../api";
import Modal from "../Modal";
import { getFormattedCurDate, showNotification } from "../../utils";
import CheckInput from "../CheckInput";
import "react-notifications-component/dist/theme.css";
import Cookies from "js-cookie";
import ClosedIcon from "../../assets/icons/colored/ClosedIcon.svg";
import OpenedIcon from "../../assets/icons/colored/OpenedIcon.svg";
import {
  closeClassSequentially,
  messageForClassCloseError,
} from "../../utils/closeClass";

// TreeNode y TreeView adaptados de Pace_modify.jsx para manejar los datos asincrónicos
const TreeNode = ({ label, children, isOpen, toggleNode, hasChildren }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        onClick={hasChildren ? toggleNode : null}
        style={{
          cursor: hasChildren ? "pointer" : "default",
          display: "flex",
        }}
      >
        {hasChildren && (
          <img
            src={isOpen ? OpenedIcon : ClosedIcon}
            alt={isOpen ? "Opened" : "Closed"}
            style={{ marginRight: 8 }}
          />
        )}
        <span style={{ fontWeight: "bold" }}>{label}:</span>
      </div>
      {isOpen && hasChildren && (
        <div style={{ marginLeft: 20 }}>{children}</div>
      )}
      {!hasChildren && (
        <div style={{ marginLeft: 28, color: "black" }}>{children}</div>
      )}
    </div>
  );
};

const TreeView = ({ data, handleReset, handleDefault }) => {
  const [openNodes, setOpenNodes] = useState({}); // Estado global para todos los nodos

  const toggleNode = (nodeKey) => {
    setOpenNodes((prevState) => ({
      ...prevState,
      [nodeKey]: !prevState[nodeKey], // Cambia el estado solo del nodo clicado
    }));
  };

  const renderTree = (obj, parentKey = "") => {
    return Object.keys(obj).map((key, index) => {
      const value = obj[key];
      const hasChildren = value !== null && typeof value === "object";
      const nodeKey = `${parentKey}${key}`;

      const isAsynchronous = nodeKey.includes("asynchronous");

      return (
        <TreeNodeWrapper
          key={index}
          label={key}
          hasChildren={hasChildren}
          nodeKey={nodeKey}
          handleReset={handleReset}
          handleDefault={handleDefault}
          isOpen={openNodes[nodeKey] || false} // Mantén el estado de cada nodo
          toggleNode={() => toggleNode(nodeKey)} // Cambia el estado del nodo clicado
        >
          {hasChildren ? (
            renderTree(value, `${nodeKey}.`)
          ) : (
            <>
              {value !== null ? (
                <>
                  {String(value)}
                  {isAsynchronous && (
                    <>
                      <label>
                        True{" "}
                        <input
                          type="radio"
                          name={`asynchronous-${nodeKey}`}
                          checked={value === true}
                          onChange={() => handleDefault(nodeKey, true)}
                        />
                      </label>
                      <label>
                        False{" "}
                        <input
                          type="radio"
                          name={`asynchronous-${nodeKey}`}
                          checked={value === false}
                          onChange={() => handleDefault(nodeKey, false)}
                        />
                      </label>
                    </>
                  )}
                </>
              ) : (
                <>"No visto"</>
              )}
            </>
          )}
        </TreeNodeWrapper>
      );
    });
  };

  return <div>{renderTree(data)}</div>;
};

const TreeNodeWrapper = ({
  label,
  children,
  hasChildren,
  nodeKey,
  handleReset,
  handleDefault,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleNode = () => setIsOpen(!isOpen);

  return (
    <TreeNode
      label={label}
      isOpen={isOpen}
      toggleNode={toggleNode}
      hasChildren={hasChildren}
    >
      {children}
    </TreeNode>
  );
};

export const Attendance = () => {
  const [showModal, setShowModal] = useState(false);
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState(null);
  const [paceData, setPaceData] = useState(null); // Guardar toda la información del pace
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [dataToSend, setDataToSend] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sheetLocked, setSheetLocked] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getTeacherClasses = async () => {
    try {
      const now = new Date();
      now.setHours(now.getHours() - 2);
      const data = await Api.Class.GetByFilters(
        `?start_date=${getFormattedCurDate(now)}`
      );
      for (var value of data) {
        const now = new Date();
        const start = new Date(value.start_date);
        const end = new Date(value.end_date);

        if (now >= start && now <= end) {
          setClassData(value);
          getStudentsInClass(value.id);
        }
      }
    } catch (error) {
      showNotification("Error", error.message, "danger");
    }
  };

  const getStudentsInClass = async (classId) => {
    try {
      const data = await Api.Class.GetAssistance(classId);
      data.forEach((student) => {
        student.assistance = false;
        student.approach = false;
      });
      setStudents(data);
    } catch (error) {
      showNotification("Error", error.message, "danger");
    }
  };

  const handleAttendanceChange = (studentId) => {
    if (isSaving || sheetLocked) return;

    const updatedStudents = students.map((student) => {
      if (student.customer_id === studentId) {
        return { ...student, assistance: !student.assistance };
      }
      return student;
    });
    setStudents(updatedStudents);
  };

  const handleApproachChange = (studentId) => {
    if (isSaving || sheetLocked) return;

    const updatedStudents = students.map((student) => {
      if (student.customer_id === studentId) {
        return { ...student, approach: !student.approach };
      }
      return student;
    });
    setStudents(updatedStudents);
  };

  const handleSaveAttendance = async () => {
    if (!students || isSaving) return;

    setIsSaving(true);
    setSheetLocked(true);

    try {
      await closeClassSequentially({
        classId: classData.id,
        students,
        updateApproach: Api.Class.UpdateApproach,
        updateAssistance: Api.Class.UpdateAssistance,
      });
      showNotification("¡Éxito!", "Asistencia actualizada", "success");
    } catch (error) {
      showNotification(
        "Error",
        messageForClassCloseError(error),
        "danger"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Obtener toda la información del pace de un estudiante
  const fetchPaceData = (customerId) => {
    var getPaceUrl = `${process.env.REACT_APP_API_URL}/api/v1/pace/student/progress`;
    fetch(getPaceUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + Cookies.get("token"),
      },
      body: JSON.stringify({ customer_id: customerId }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((data) => {
            throw new Error(data.message);
          });
        } else if (response.status === 200) {
          showNotification("Usuario encontrado", "¡Bien!", "success");
          return response.json();
        }
      })
      .then((data) => {
        if (data && data.active_paces.length > 0) {
          setPaceData(data.active_paces[0]); // Guardar toda la información del pace
          setDataToSend({ ...data }); // Asegurarse de que los datos a enviar se inicializan correctamente
        } else {
          setPaceData(null); // No hay datos
        }
      })
      .catch((error) => {
        showNotification("Error", error.message, "warning");
        setPaceData(null); // En caso de error, también mostramos como no disponible
      });
  };

  const handleReset = (path) => {
    modifyData(path, null);
    setConfirmationDialogOpen(true);
  };

  const handleDefault = (path, newValue = null) => {
    if (path.includes("asynchronous")) {
      modifyData(path, newValue); // Modificar la información asincrónica
    } else {
      modifyData(path, 0); // Para otros casos, enviar 0 como valor predeterminado
    }
    setConfirmationDialogOpen(true);
  };

  const modifyData = (path, newValue) => {
    const pathArray = path.split(".");
    const newResponseData = { ...dataToSend };
    let currentLevel = newResponseData.active_paces[0].content_progress;

    for (let i = 0; i < pathArray.length - 1; i++) {
      if (!currentLevel[pathArray[i]]) {
        currentLevel[pathArray[i]] = {};
      }
      currentLevel = currentLevel[pathArray[i]];
    }

    currentLevel[pathArray[pathArray.length - 1]] = newValue;

    setDataToSend(newResponseData);
  };

  const handleConfirmPaceChange = async () => {
    try {
      var updatePaceUrl = `${process.env.REACT_APP_API_URL}/api/v1/pace/student`;
      await fetch(updatePaceUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + Cookies.get("token"),
        },
        body: JSON.stringify({
          customer_id: selectedStudent,
          pace: dataToSend.active_paces[0],
        }),
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((data) => {
              throw new Error(data.message);
            });
          } else if (response.status === 200) {
            showNotification(
              "¡Éxito!",
              "Pace actualizado correctamente",
              "success"
            );
            setShowModal(false);
          }
        })
        .catch((error) => {
          showNotification("Error", error.message, "danger");
        });
    } catch (error) {
      showNotification("Error", error.message, "danger");
    }
  };

  useEffect(() => {
    getTeacherClasses();
  }, []);

  if (!classData) {
    return (
      <div className="attendace_container">
        <div className="attendace_render">
          <h1>Sin clases en este horario</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="attendace_container">
      <div className="attendace_render">
        <h1>Información de la Clase</h1>
        <p>
          Inicio: {classData.start_date} - Fin: {classData.end_date}
        </p>
        {classData.location === "REMOTE" ? (
          <p>Zoom Key: {classData.location_name}</p>
        ) : (
          <p>Location: {classData.location_name}</p>
        )}
        <p>Tipo: {classData.type}</p>
        <p>Idioma: {classData.language}</p>

        <h2>Lista de Estudiantes</h2>

        {isMobile ? (
          <div className="student-card-container">
            {students &&
              students.map((student) => (
                <>
                  <div key={student.customer_id} className="student-card">
                    <h3 className="student-card-title">
                      {student.full_name || "-"}
                    </h3>
                    <p>
                      <strong>Libro:</strong> {student.book_name}
                    </p>
                    <p>
                      <strong>Sesión:</strong> {student.session_name}
                    </p>
                    <div className="student-card-actions">
                      <button
                        className="btn_pace_modify"
                        onClick={() => {
                          setSelectedStudent(student.customer_id);
                          fetchPaceData(student.customer_id);
                          setShowModal(true);
                        }}
                      >
                        Visualizar Pace
                      </button>
                    </div>
                    <div className="student-checkinput">
                      <p>
                        <strong>Asistencia</strong>
                      </p>
                      <CheckInput
                        isToggled={student["assistance"]}
                        onToggle={() =>
                          handleAttendanceChange(student.customer_id)
                        }
                        disabled={isSaving || sheetLocked}
                      />
                    </div>
                    {classData.type === "REGULAR" && (
                      <div className="student-checkinput">
                        <p>
                          <strong>Abordaje</strong>
                        </p>
                        <CheckInput
                          isToggled={student["approach"]}
                          onToggle={() =>
                            handleApproachChange(student.customer_id)
                          }
                          disabled={isSaving || sheetLocked}
                        />
                      </div>
                    )}
                  </div>
                </>
              ))}
          </div>
        ) : (
          <table className="shown_student">
            <thead>
              <tr>
                <th>Nombre Completo</th>
                <th>Libro</th>
                <th>Sesión</th>
                <th>Visualizar Pace</th>
                <th>Asistencia</th>
                {classData.type === "REGULAR" && <th>Abordaje</th>}
              </tr>
            </thead>
            <tbody>
              {students &&
                students.map((student) => (
                  <tr key={student.customer_id}>
                    <td>{student.full_name || "-"}</td>
                    <td>{student.book_name}</td>
                    <td>{student.session_name}</td>
                    <td>
                      <button
                        className="btn_pace_modify"
                        onClick={() => {
                          setSelectedStudent(student.customer_id);
                          fetchPaceData(student.customer_id);
                          setShowModal(true);
                        }}
                      >
                        Visualizar Pace
                      </button>
                    </td>
                    <td>
                      <CheckInput
                        isToggled={student["assistance"]}
                        onToggle={() =>
                          handleAttendanceChange(student.customer_id)
                        }
                        disabled={isSaving || sheetLocked}
                      />
                    </td>
                    {classData.type === "REGULAR" && (
                      <td>
                        <CheckInput
                          isToggled={student["approach"]}
                          onToggle={() =>
                            handleApproachChange(student.customer_id)
                          }
                          disabled={isSaving || sheetLocked}
                        />
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        )}

        <button
          className="classes_render-button"
          onClick={handleSaveAttendance}
          disabled={isSaving}
        >
          {isSaving ? "Guardando..." : "Guardar Asistencia"}
        </button>

        {showModal && (
          <Modal
            isOpen={showModal}
            title="Visualizar Pace Asíncrono"
            Content={() => (
              <div>
                {paceData ? (
                  <TreeView
                    data={paceData.content_progress}
                    handleReset={handleReset}
                    handleDefault={handleDefault}
                  />
                ) : (
                  <p>Sin modalidad asincrónica</p>
                )}
              </div>
            )}
            onCancel={() => setShowModal(false)}
            onSubmit={handleConfirmPaceChange}
          />
        )}
      </div>
    </div>
  );
};

export default Attendance;
